import React from 'react';
import { Button, Table } from 'antd';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'contexts/AuthContext';
import { AccessType } from 'types/User';
import { StatusDataType } from 'const/statusDefinitions';
import { ReactComponent as CollapseRowIcon } from 'assets/icons/CollapseRow.svg';

interface StatusTableProps {
  className?: string;
  data: readonly string[];
  statusTag?: (status: string) => React.ReactNode;
  statusDefinitions?: string;
  statusTitle: string;
  statusColumnWidthPercent?: number;
  expandableRows?: Record<
    string,
    Array<{ tag: React.ReactNode; definition: string }>
  >;
}

export default function StatusTable({
  className = '',
  data,
  statusTag,
  statusDefinitions,
  statusTitle,
  statusColumnWidthPercent = 20,
  expandableRows,
}: StatusTableProps) {
  const { t } = useTranslation();

  const { auth } = useAuthContext();
  const isConcessionnaire =
    auth?.user.accessType === AccessType.CONCESSIONNAIRE;

  const statusData: StatusDataType[] = data.map((status, index) => {
    const statusForDefinition =
      isConcessionnaire &&
      (status === 'ChantierEnCours' ||
        status === 'DocumentsEnAttente' ||
        status === 'Abandon')
        ? `${status}_concession`
        : status;

    return {
      key: index.toString(),
      definition: t(`${statusDefinitions}:${statusForDefinition}`),
      status,
      ...(expandableRows?.[status] && {
        children: expandableRows[status].map((subStatus, i) => ({
          key: `${index}-${i}`,
          definition: subStatus.definition,
          customTag: subStatus.tag,
          isChildRow: true,
        })),
      }),
    };
  });

  const columnsStatus = [
    {
      title: t(statusTitle),
      key: 'status',
      dataIndex: 'status',
      width: `${statusColumnWidthPercent}%`,
      render: (status: string, record: StatusDataType) => {
        if (record.customTag !== undefined) {
          return (
            <div className="pl-14 whitespace-nowrap">{record.customTag}</div>
          );
        }
        return statusTag ? (
          <div className="pr-3">{statusTag(status)}</div>
        ) : (
          status
        );
      },
    },
    {
      title: t('glossaire:definition'),
      key: 'definition',
      dataIndex: 'definition',
      width: `${100 - statusColumnWidthPercent}%`,
    },
  ];

  return (
    <Table
      columns={columnsStatus}
      dataSource={statusData}
      pagination={false}
      bordered
      className={clsx(
        'separate-rows-table blue-header-table radius-sm [&_.ant-table-thead_.ant-table-cell]:!border-none',
        className,
      )}
      rowClassName={(record: StatusDataType) =>
        record.isChildRow ? '[&>td]:!bg-[#FCFCFD]' : ''
      }
      expandable={{
        expandIcon: ({ expanded, onExpand, record }) =>
          record.children?.length ? (
            <Button
              type="text"
              className="ant-table-row-expand-icon"
              onClick={(e) => onExpand(record, e)}
            >
              <CollapseRowIcon
                className={clsx(
                  'text-bleu-hellio transition-transform',
                  expanded && 'rotate-90',
                )}
              />
            </Button>
          ) : (
            <span className="ant-table-row-expand-icon ant-table-row-expand-icon-spaced" />
          ),
      }}
    />
  );
}
